/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';

import { getIconColor } from '../skill-colors';
import { skillIconMap } from '../skill-icons';
import type { RovoSkillTagProps } from '../skill-tag/SkillTag';

const hovercardStyles = cssMap({
	container: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '320px',
		textAlign: 'left',
	},
	containerScrollable: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '320px',
		textAlign: 'left',
		overflowY: 'auto',
	},
	skillName: {
		fontWeight: token('font.weight.bold'),
		marginBottom: token('space.100'),
		alignItems: 'center',
	},
	product: {
		marginTop: token('space.150'),
		color: token('color.text.subtle'),
		alignItems: 'center',
		textTransform: 'capitalize',
	},
});

export type SkillHoverCardContentProps = Omit<RovoSkillTagProps, 'slug' | 'hovercardMaxHeight'> & {
	maxHeight?: number | string;
};

export const SkillHoverCardContent = ({
	product,
	iconName,
	maxHeight,
	...props
}: SkillHoverCardContentProps): JSX.Element => {
	const IconComponent = iconName ? skillIconMap[iconName] : undefined;

	return (
		<Box
			testId="skill-hovercard-content"
			xcss={
				maxHeight !== undefined ? hovercardStyles.containerScrollable : hovercardStyles.container
			}
			style={{ maxHeight }}
		>
			<Stack>
				<Inline xcss={hovercardStyles.skillName} space="space.100">
					{IconComponent && (
						<Suspense fallback={null} interactionName="skill-tag-hovercard-icon">
							<IconComponent
								label=""
								size="medium"
								color={getIconColor(props?.color, props?.invertColors)}
							/>
						</Suspense>
					)}
					<Heading size="xsmall">{props.name}</Heading>
				</Inline>
				{props.description && (
					<Box>
						<Text color="color.text">{props.description}</Text>
					</Box>
				)}
				{product && (
					<Inline xcss={hovercardStyles.product} space="space.075">
						{props.productIconUrl && (
							<Image
								src={props.productIconUrl}
								testId={`${product}-icon`}
								alt=""
								width={16}
								height={16}
							/>
						)}
						<Text color="color.text.subtle" size="small">
							{product}
						</Text>
					</Inline>
				)}
			</Stack>
		</Box>
	);
};
